// Hand-written mirror of the actions schema (fraud-dbs 20260915000001 and
// its rename) and of the channel catalogue in libs/tooling/mitigation/
// strategies.py, like autoDomainTypes.ts mirrors the candidates. Keep in step.
//
// An "action taken" is every outbound thing the platform did about an entry:
// the planned takedown channels (registrar abuse desk, CNCS, the browser
// forms) and the manual ones an operator fires from their own screen (the
// SIBS alert, the SIBS case report, a composed email, a domain marked
// inactive). The manual ones are logged, never planned — see MANUAL_CHANNELS.

import type { CHIP_TONE_CLASS } from './autoDomainTypes'

export type ReportTaskStatus = 'planned' | 'queued' | 'sending' | 'awaiting_browser' | 'sent' | 'acknowledged' | 'resolved' | 'failed' | 'skipped' | 'cancelled'
export type ReportKind = 'email' | 'browser_form' | 'internal'
// entry_action.resolution (fraud-dbs 20261025000000): the outcome an operator
// confirms. taken_down is final; suspended / blocklisted / expired can be
// undone; refused / no_reply / other say the report went nowhere.
export type ReportResolution = 'taken_down' | 'suspended' | 'blocklisted' | 'expired' | 'refused' | 'no_reply' | 'other'

export type ReportChannel = { id: string; label: string; kind: ReportKind; follow_up: string; manual_only: boolean }

export type ReportEvent = { id: string; action_id: string; at: string; kind: string; actor: string | null; detail: Record<string, any> }
export type ReportReply = { id: string; action_id: string | null; log_id: string | null; from_address: string | null; subject: string | null; body_text: string | null; created_at: string }

export type EntryAction = {
  id: string
  user_entry_id: string | null
  candidate_id: string | null
  domain_id: string | null
  platform_id: number | null
  tenant_id: string | null
  // A domain-scoped action has a hostname; a manual one (a SIBS alert, an
  // emailed case report) has none and says what it was about instead.
  hostname: string | null
  about: string | null
  entry_ref: string | null
  channel: string
  kind: ReportKind
  status: ReportTaskStatus
  target: string | null
  payload: {
    applicability?: { ok: boolean; reason: string; target?: string | null; source?: string | null }
    subject?: string
    body?: string
    to?: string[]
    form_url?: string
    fields?: Record<string, any>
    evidence_lines?: string[]
    [k: string]: any
  }
  email_log_id: string | null
  email_status?: string | null
  email_to?: string | null
  handoff_id: string | null
  // state of the browser handoff, when the worker parked one (entry state only)
  handoff_status?: string | null
  handoff_claimed_by?: string | null
  attempts: number
  last_error: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  queued_at: string | null
  sent_at: string | null
  acknowledged_at: string | null
  resolved_at: string | null
  resolution: ReportResolution | null
  resolution_candidate: boolean
  notes: string | null
  events?: ReportEvent[]
  replies?: ReportReply[]
}

// entry_action_flat, the control center's row
export type EntryActionFlat = EntryAction & {
  action_id: string
  // hostname when there is one, otherwise what the action was about
  subject_label: string | null
  email_status: string | null
  email_to: string | null
  platform_name_code: string | null
  platform_name: string | null
  applicability: EntryAction['payload']['applicability'] | null
  handoff_status: string | null
  handoff_claimed_by: string | null
  age_hours: number | null
  hours_since_sent: number | null
  last_event_kind: string | null
  last_event_at: string | null
  last_event_detail: Record<string, any> | null
  last_reply_from: string | null
  last_reply_at: string | null
  last_reply_excerpt: string | null
  reply_count: number
}

export type EntryActionState = { tasks: EntryAction[]; catalog: ReportChannel[] }

// The channels nothing plans: they are logged when an operator sends from
// the screen that owns them. The planner never produces one, and the
// configuration panel has nothing to configure for them.
export const MANUAL_CHANNELS = ['sibs_alert', 'sibs_case_report', 'domain_report', 'alert_email', 'domain_marked_inactive']

export const isManualChannel = (id: string) => MANUAL_CHANNELS.includes(id)

export const TASK_STATUS_LABEL: Record<ReportTaskStatus, string> = {
  planned: 'Ready', queued: 'Queued', sending: 'Sending', awaiting_browser: 'Awaiting browser', sent: 'Sent',
  acknowledged: 'Replied', resolved: 'Resolved', failed: 'Failed', skipped: 'Not applicable', cancelled: 'Cancelled',
}

export const TASK_STATUS_TONE: Record<ReportTaskStatus, keyof typeof CHIP_TONE_CLASS> = {
  planned: 'sky', queued: 'amber', sending: 'amber', awaiting_browser: 'amber', sent: 'sky',
  acknowledged: 'emerald', resolved: 'emerald', failed: 'red', skipped: 'neutral', cancelled: 'neutral',
}

export const RESOLUTION_LABEL: Record<ReportResolution, string> = {
  taken_down: 'Taken down', suspended: 'Suspended', blocklisted: 'Blocklisted', expired: 'Expired',
  refused: 'Refused', no_reply: 'No reply', other: 'Other',
}

// The outcomes that stop a host (vs a report that went nowhere).
export const CONTAINED_RESOLUTIONS: ReportResolution[] = ['taken_down', 'suspended', 'blocklisted', 'expired']
export const isContained = (r: unknown): r is ReportResolution => CONTAINED_RESOLUTIONS.includes(r as ReportResolution)

// "planned" and "failed" can be sent; anything in flight or done cannot.
export const canSend = (t: EntryAction) => t.status === 'planned' || (t.status === 'failed' && t.attempts < 3)
export const canCancel = (t: EntryAction) => ['planned', 'queued', 'awaiting_browser', 'failed'].includes(t.status)
export const canResolve = (t: EntryAction) => ['sent', 'acknowledged', 'awaiting_browser', 'failed'].includes(t.status)
export const isInFlight = (t: EntryAction) => ['queued', 'sending', 'awaiting_browser'].includes(t.status)
